// §4.3 — PRICE LISTS, /sales/price-lists (Module 09, P3-A part 2).
//
// One row per §2.7 list: code, name, tier, currency, the D-234 VAT-mode chip,
// validity (an open-ended list SAYS open-ended rather than rendering an empty
// cell), status, activation stamp. Cursor pages folded through the shared
// accumulator; filters are local state (the remnants idiom — a pricing window
// is a glance, not an address).
//
// The AUDIENCE is §4.3's role set (ga/fa/sales); creation is offered on the
// `sales.tier.manage` mirror only (sales reads the book, admins govern it —
// the service enforces exactly that, D-040 two-layer).

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { SalesFilterBar } from "../components/sales-filter-bar";
import { SalesGroupTabs } from "../components/group-tabs";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { Select } from "../../../../components/select";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { StatusChip, type ChipVariant } from "../../../../components/status-chip";
import { UnauthorizedError } from "../../../../lib/problem";
import { bookPager, useBookPages, useBookWindow } from "../hooks/book-pages";
import { requestIdOf } from "../../../../lib/request";
import { formatBusinessDate, formatTimestamp } from "../../../../lib/datetime";
import { todayIso } from "../../../../lib/business-dates";
import { MutationFailureNote } from "../mutation-failure-note";
import { isSalesReadRefused, useSalesPermissions } from "../hooks/permissions";
import {
  useCreatePriceList,
  usePriceLists,
  type PriceList,
  type PriceListCreateRequest,
  type PriceListsQuery,
} from "../hooks/sales-queries";

/** §2.7's status vocabulary → the §3.5 chip variant, stated once: active is
 * GREEN (the list prices quotes today), draft is ACCENT (being authored),
 * archived is NEUTRAL (a finished life, not a failure). */
export function priceListStatusVariant(status: string): ChipVariant {
  switch (status) {
    case "active":
      return "ok";
    case "draft":
      return "accent";
    default:
      return "neutral";
  }
}

const TIERS = ["market", "b2b", "distributor"] as const;

export function SalesPriceListsScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();

  const [tier, setTier] = useState("");
  const [currency, setCurrency] = useState("");
  const [status, setStatus] = useState("");
  const [creating, setCreating] = useState(false);

  const currencyClean = currency.trim().toUpperCase();
  const query: PriceListsQuery = useMemo(
    () => ({
      ...(tier.length > 0 ? { tier: tier as NonNullable<PriceListsQuery["tier"]> } : {}),
      ...(/^[A-Z]{3}$/u.test(currencyClean) ? { currency: currencyClean } : {}),
      ...(status.length > 0 ? { status: status as NonNullable<PriceListsQuery["status"]> } : {}),
    }),
    [tier, currencyClean, status],
  );

  const queryKey = JSON.stringify(query);

  const refused = isSalesReadRefused(perms, perms.canOpenPricing && perms.has("sales.read"));
  // One page at a time (D-560): the stack starts again at page 1 whenever
  // the filters do.
  const pages = useBookPages(queryKey);
  const page = usePriceLists(query, pages.cursor, !refused);
  const rowsAccum = useMemo(() => page.data?.items ?? [], [page.data]);
  const pageView = useBookWindow(pages, {
    items: rowsAccum,
    nextCursor: page.data?.next_cursor,
    turning: page.isPlaceholderData,
    settled: page.data !== undefined && !page.isFetching,
  });

  useEffect(() => {
    if (page.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [page.error, navigate]);

  const create = useCreatePriceList();

  const columns: DataTableColumn<PriceList>[] = [
    { key: "code", kind: "id", header: t("sales.priceLists.colCode"), id: true },
    { key: "name", phonePriority: 1, header: t("sales.priceLists.colName") },
    {
      key: "tier",
      phonePriority: 2,
      header: t("sales.priceLists.colTier"),
      render: (row) => t(`sales.tier.${row.tier}`),
    },
    { key: "currency", phonePriority: 3, header: t("sales.priceLists.colCurrency") },
    {
      key: "vat_mode",
      header: t("sales.priceLists.colVat"),
      render: (row) => (
        <StatusChip variant="neutral" dot={false} data-testid="sales-vat-chip">
          {t(`sales.priceLists.vat.${row.vat_mode}`)}
        </StatusChip>
      ),
    },
    {
      key: "valid_from",
      kind: "date",
      header: t("sales.priceLists.colValidity"),
      render: (row) => (
        <span className="num">
          {formatBusinessDate(row.valid_from, i18n.language)} –{" "}
          {row.valid_to === null
            ? t("sales.priceLists.openEnded")
            : formatBusinessDate(row.valid_to, i18n.language)}
        </span>
      ),
    },
    {
      key: "status",
      kind: "status",
      header: t("sales.priceLists.colStatus"),
      render: (row) => (
        <StatusChip
          variant={priceListStatusVariant(row.status)}
          data-testid="sales-price-list-status"
        >
          {t(`sales.priceLists.status.${row.status}`)}
        </StatusChip>
      ),
    },
    {
      key: "activated_at",
      kind: "date",
      header: t("sales.priceLists.colActivated"),
      age: true,
      render: (row) =>
        row.activated_at === null ? (
          <span className="sales-hint">{t("sales.priceLists.notActivated")}</span>
        ) : (
          formatTimestamp(row.activated_at, i18n.language)
        ),
    },
  ];

  const chipLabel = (filter: string, value: string): string =>
    t("sales.common.filterChip", { filter, value });

  return (
    <section className="sales-price-lists-screen" data-testid="sales-price-lists-screen">
      <PageHeader
        title={t("sales.priceLists.title")}
        actions={
          <>
            {perms.has("sales.tier.manage") ? (
              <Button
                variant="primary"
                data-testid="sales-price-list-new"
                onClick={() => {
                  setCreating(true);
                }}
              >
                {t("sales.priceLists.new")}
              </Button>
            ) : null}
          </>
        }
      />
      <SalesGroupTabs group="settings" />

      <SalesFilterBar
        data-testid="sales-price-lists-filters"
        filters={[
          {
            key: "tier",
            applied:
              tier === ""
                ? undefined
                : chipLabel(t("sales.priceLists.filterTier"), t(`sales.tier.${tier}`)),
            onClear: () => {
              setTier("");
            },
            control: (
              <Field label={t("sales.priceLists.filterTier")}>
                <Select
                  value={tier}
                  data-testid="price-lists-tier"
                  onChange={(e) => {
                    setTier(e.target.value);
                  }}
                >
                  <option value="">{t("sales.priceLists.anyTier")}</option>
                  {TIERS.map((value) => (
                    <option key={value} value={value}>
                      {t(`sales.tier.${value}`)}
                    </option>
                  ))}
                </Select>
              </Field>
            ),
          },
          {
            key: "currency",
            inline: true,
            applied:
              query.currency === undefined
                ? undefined
                : chipLabel(t("sales.priceLists.filterCurrency"), query.currency),
            onClear: () => {
              setCurrency("");
            },
            control: (
              <Field label={t("sales.priceLists.filterCurrency")}>
                <input
                  value={currency}
                  placeholder={t("sales.priceLists.currencyHint")}
                  data-testid="price-lists-currency"
                  onChange={(e) => {
                    setCurrency(e.target.value);
                  }}
                />
              </Field>
            ),
          },
          {
            key: "status",
            inline: true,
            applied:
              status === ""
                ? undefined
                : chipLabel(
                    t("sales.priceLists.filterStatus"),
                    t(`sales.priceLists.status.${status}`),
                  ),
            onClear: () => {
              setStatus("");
            },
            control: (
              <Field label={t("sales.priceLists.filterStatus")}>
                <Select
                  value={status}
                  data-testid="price-lists-status"
                  onChange={(e) => {
                    setStatus(e.target.value);
                  }}
                >
                  <option value="">{t("sales.priceLists.anyStatus")}</option>
                  {(["draft", "active", "archived"] as const).map((value) => (
                    <option key={value} value={value}>
                      {t(`sales.priceLists.status.${value}`)}
                    </option>
                  ))}
                </Select>
              </Field>
            ),
          },
        ]}
      />

      {refused ? (
        <ForbiddenState title={t("sales.access.title")} body={t("sales.access.pricing")} />
      ) : null}

      {!refused && page.fetchStatus === "paused" && rowsAccum.length === 0 ? (
        <OfflineState title={t("sales.offline.title")} body={t("sales.offline.body")} />
      ) : null}

      {!refused && page.isPending && page.fetchStatus !== "paused" && rowsAccum.length === 0 ? (
        <TableSkeleton rows={5} cols={columns.length} label={t("sales.common.loading")} />
      ) : null}

      {!refused && page.error !== null && !(page.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("sales.priceLists.error")}
          retryLabel={t("sales.common.retry")}
          requestIdLabel={t("sales.common.requestId")}
          requestId={requestIdOf(page.error)}
          onRetry={() => {
            void page.refetch();
          }}
        />
      ) : null}

      {!refused &&
      !page.isPending &&
      page.fetchStatus !== "paused" &&
      page.error === null &&
      rowsAccum.length === 0 ? (
        <EmptyState
          title={t("sales.priceLists.emptyTitle")}
          body={t("sales.priceLists.emptyBody")}
        />
      ) : null}

      {rowsAccum.length > 0 ? (
        <>
          <DataTable
            data-testid="sales-price-lists-table"
            columns={columns}
            rows={rowsAccum}
            rowKey={(row) => row.id}
            empty={null}
            caption={t("sales.priceLists.tableCaption")}
            rowTestId="sales-price-list-row"
            pager={bookPager(t, i18n.language, pageView, {
              turning: page.isPlaceholderData,
              onNext: () => {
                const next = page.data?.next_cursor;
                if (next === null || next === undefined) return;
                pages.next(next, rowsAccum.length);
              },
              onPrev: pages.prev,
            })}
            rowProps={(row) => ({ "data-tier": row.tier, "data-status": row.status })}
            onRowClick={(row) => {
              void navigate({
                to: "/sales/price-lists/$priceListId",
                params: { priceListId: row.id },
              });
            }}
          />
        </>
      ) : null}

      {creating ? (
        <NewPriceListModal
          busy={create.isPending}
          error={create.isError ? create.error : undefined}
          onClose={() => {
            create.reset();
            setCreating(false);
          }}
          onSubmit={(body) => {
            create.mutate(body, {
              onSuccess: (row) => {
                setCreating(false);
                void navigate({
                  to: "/sales/price-lists/$priceListId",
                  params: { priceListId: row.id },
                });
              },
            });
          }}
        />
      ) : null}
    </section>
  );
}

/** §2.7 create — `vat_mode` may be LEFT EMPTY: the API defaults it by tier
 * (D-234's regional convention, flipped as data), and the hint says so. */
function NewPriceListModal({
  busy,
  error,
  onClose,
  onSubmit,
}: {
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (body: PriceListCreateRequest) => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [tier, setTier] = useState<(typeof TIERS)[number]>("b2b");
  const [currency, setCurrency] = useState("");
  const [vatMode, setVatMode] = useState("");
  const [validFrom, setValidFrom] = useState(() => todayIso());
  const [validTo, setValidTo] = useState("");

  const currencyClean = currency.trim().toUpperCase();
  const valid =
    code.trim().length > 0 &&
    name.trim().length > 0 &&
    /^[A-Z]{3}$/u.test(currencyClean) &&
    /^\d{4}-\d{2}-\d{2}$/u.test(validFrom);

  return (
    <Modal
      heading={t("sales.listCreate.title")}
      label={t("sales.listCreate.title")}
      closeLabel={t("sales.common.close")}
      onClose={onClose}
      className="sales-modal"
      testId="sales-price-list-create-modal"
    >
      <div className="sales-form">
        <Field label={t("sales.listCreate.code")}>
          <input
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
            }}
          />
        </Field>
        <Field label={t("sales.listCreate.name")} wide>
          <input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        </Field>
        <Field label={t("sales.listCreate.tier")}>
          <Select
            value={tier}
            onChange={(e) => {
              setTier(e.target.value as (typeof TIERS)[number]);
            }}
          >
            {TIERS.map((value) => (
              <option key={value} value={value}>
                {t(`sales.tier.${value}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("sales.listCreate.currency")}>
          <input
            value={currency}
            onChange={(e) => {
              setCurrency(e.target.value);
            }}
          />
        </Field>
        <Field label={t("sales.listCreate.vatMode")}>
          <Select
            value={vatMode}
            onChange={(e) => {
              setVatMode(e.target.value);
            }}
          >
            <option value="">{t("sales.listCreate.vatModeDefault")}</option>
            <option value="inclusive">{t("sales.priceLists.vat.inclusive")}</option>
            <option value="exclusive">{t("sales.priceLists.vat.exclusive")}</option>
          </Select>
        </Field>
        <Field label={t("sales.listCreate.validFrom")}>
          <input
            type="date"
            value={validFrom}
            onChange={(e) => {
              setValidFrom(e.target.value);
            }}
          />
        </Field>
        <Field label={t("sales.listCreate.validTo")}>
          <input
            type="date"
            value={validTo}
            onChange={(e) => {
              setValidTo(e.target.value);
            }}
          />
        </Field>
      </div>
      {error !== undefined ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.listCreate.failed")}
          error={error}
        />
      ) : null}
      <div className="sales-modal-actions">
        <Button onClick={onClose}>{t("sales.common.cancel")}</Button>
        <Button
          variant="primary"
          data-testid="sales-price-list-create-submit"
          busy={busy}
          busyLabel={t("sales.common.saving")}
          disabled={!valid}
          onClick={() => {
            onSubmit({
              code: code.trim(),
              name: name.trim(),
              tier,
              currency: currencyClean,
              ...(vatMode.length > 0
                ? { vat_mode: vatMode as NonNullable<PriceListCreateRequest["vat_mode"]> }
                : {}),
              valid_from: validFrom,
              ...(validTo.length > 0 ? { valid_to: validTo } : {}),
            });
          }}
        >
          {t("sales.listCreate.submit")}
        </Button>
      </div>
    </Modal>
  );
}
